/**
 * Traductions FR / EN – CV Allen Le Yaouanc
 */

import type { Lang } from '@/pages/Index';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ExperienceData {
  period: string;
  role: string;
  company: string;
  location: string;
  mission?: string;
  activities: string[];
  valueAdded?: string;
}

export interface EducationData {
  year: string;
  degree: string;
  institution: string;
  note?: string;
}

export interface SkillCategory {
  title: string;
  skills: string[];
}

export interface LanguageData {
  name: string;
  level: string;
}

interface Translations {
  header: {
    subtitle: string;
    subtitleSuffix: string;
    location: string;
    scrollLabel: string;
  };
  profile: {
    sectionTitle: string;
    intro: string;
    details: string;
  };
  experience: {
    sectionTitle: string;
    items: ExperienceData[];
  };
  education: {
    sectionTitle: string;
    items: EducationData[];
  };
  skills: {
    sectionTitle: string;
    categories: SkillCategory[];
    languagesTitle: string;
    languages: LanguageData[];
  };
  projects: {
    sectionTitle: string;
    viewProject: string;
    linkSoon: string;
    items: {
      number: string;
      title: string;
      client: string;
      year: string;
      description: string;
      tags: string[];
      url?: string;
      animationKey: 'search' | 'linkInBio' | 'illustration' | 'piano';
    }[];
  };
  gallery: {
    sectionTitle: string;
    moreInfo: string;
    captions: Record<string, string>;
  };
  contact: {
    title: string;
    subtitle: string;
    cta: string;
  };
  stickyHeader: {
    darkLabel: string;
    lightLabel: string;
  };
  nav: {
    intro: string;
    profile: string;
    experience: string;
    education: string;
    skills: string;
    projects: string;
    gallery: string;
    contact: string;
  };
  customization: {
    typography: string;
    color: string;
    gradient: string;
    density: string;
    fontSans: string;
    fontSerif: string;
    fontMono: string;
    colorSilver: string;
    colorOcean: string;
    colorForest: string;
    colorGold: string;
    gradientSubtle: string;
    gradientSubtleDesc: string;
    gradientVivid: string;
    gradientVividDesc: string;
    gradientMinimal: string;
    gradientMinimalDesc: string;
    densityCompact: string;
    densityNormal: string;
    densityAiry: string;
  };
}

/* ------------------------------------------------------------------ */
/*  FR                                                                 */
/* ------------------------------------------------------------------ */

const fr: Translations = {
  header: {
    subtitle: 'Consultant Senior',
    subtitleSuffix: 'Solutions Digitales & UX',
    location: 'Valais, Suisse',
    scrollLabel: 'Défiler vers le bas',
  },
  profile: {
    sectionTitle: 'Profil',
    intro:
      "Consultant senior, issu d'un environnement artistique exigeant et spécialisé dans les technologies numériques, j'accompagne depuis plus de quinze ans PME, fondations, acteurs culturels et organisations européennes dans la conception, l'évolution et la pérennisation de leurs plateformes web.",
    details:
      "Compétences étendues : WordPress avancé, UX/UI, tunnels de conversion, optimisation technique et production graphique et vidéo. Apporte des solutions complètes et durables, en prenant en charge l'ensemble du cycle projet.",
  },
  experience: {
    sectionTitle: 'Expérience Professionnelle',
    items: [
      {
        period: "2010 — Aujourd'hui",
        role: 'Consultant Senior en Solutions Digitales & UX',
        company: 'Freelance',
        location: 'France, Suisse, Belgique, Europe centrale',
        mission: "Accompagnement digital de fondations, artistes et organisations en Europe.",
        activities: [
          'Conception, développement et maintenance de plateformes WordPress complexes',
          'Refonte UX/UI, optimisation mobile et amélioration technique',
          'Création de formulaires et tunnels de paiement (one-shot, récurrents)',
          'Production graphique et contenus digitaux (illustrations, vidéos)',
          'SEO technique, optimisation des performances, sécurité',
          'Mise en place de solutions internes : extranet, tableaux de bord, automatisations',
          'Support et accompagnement long terme auprès de directions ou équipes internes',
        ],
        valueAdded:
          "Apporte des solutions complètes, stables et durables aux structures qui ne disposent pas d'un service digital interne ou qui souhaitent renforcer leurs capacités techniques.",
      },
      {
        period: '2014 — 2015',
        role: 'Assistant Parlementaire Accrédité — Communication Digitale',
        company: 'Parlement européen',
        location: 'Bruxelles',
        mission: 'Gestion quotidienne de la communication digitale.',
        activities: [
          'Production de contenus (web, newsletters, réseaux sociaux)',
          'Vidéo et motion design',
          'Coordination éditoriale',
        ],
      },
      {
        period: '2005 — 2010',
        role: 'Assistant de Direction & Communication',
        company: 'Restaurant / Galerie 1728',
        location: 'Paris',
        activities: [
          'Secrétariat administratif',
          'Communication online et offline',
          "Organisation d'événements",
          'Gestion des contenus visuels',
          'Gestion de cave',
        ],
      },
    ],
  },
  education: {
    sectionTitle: 'Formation',
    items: [
      { year: '2003 — 2005', degree: 'Concepteur-réalisateur multimédia', institution: 'IESA Multimedia, Paris' },
      { year: '1999 — 2001', degree: 'Droit', institution: 'Université Panthéon-Assas, Paris', note: 'Cycle non terminé' },
      { year: '2001 — 2002', degree: 'First Certificate', institution: 'British Council, Paris' },
      { year: '1999', degree: 'Baccalauréat littéraire', institution: 'École Massillon, Paris' },
    ],
  },
  skills: {
    sectionTitle: 'Compétences',
    categories: [
      {
        title: 'Gestion de Projet',
        skills: [
          'Pilotage de projets digitaux (analyse du besoin, planification, reporting)',
          'Approche itérative : MVP, sprints courts, optimisation continue',
          'Coordination avec directions, équipes métier et prestataires',
          'Documentation, spécifications fonctionnelles, suivi qualité',
        ],
      },
      {
        title: 'UX / UI & Design',
        skills: [
          'Audit UX, refonte de parcours utilisateurs, optimisation de conversion',
          'Création graphique (Illustrator, Photoshop, InDesign)',
          'Illustration éditoriale (IA + retouche)',
          'Montage vidéo (Premiere Pro)',
        ],
      },
      {
        title: 'Développement & Intégration',
        skills: [
          'WordPress avancé : modules personnalisés, optimisation, sécurité',
          'Développement front-end (HTML / CSS / JS)',
          'Intégration de systèmes de paiement (Stripe, paiements en ligne, récurrents)',
          'Optimisation technique : performance, SEO, sécurité, email deliverability',
        ],
      },
      {
        title: 'Marketing Digital',
        skills: [
          'SEO technique et éditorial',
          'Email marketing, newsletters, segmentation',
          'Gestion de contenu multilingue',
        ],
      },
    ],
    languagesTitle: 'Langues',
    languages: [
      { name: 'Français', level: 'Natif' },
      { name: 'Anglais', level: 'Avancé' },
      { name: 'Russe', level: 'Intermédiaire' },
    ],
  },
  projects: {
    sectionTitle: 'Projets',
    viewProject: 'Voir le projet →',
    linkSoon: 'Lien à venir',
    items: [
      {
        number: '01',
        title: "Plateforme IA d'analyse politique et métapolitique (V2.5)",
        client: 'Polémia',
        year: '2026',
        description:
          "Idée initiale. Travail au long cours, mené en tandem, sur la structuration des contenus, les parcours utilisateurs et l'expérience de consultation de corpus éditoriaux complexes (50.000 sources). Projet réalisé pour le compte d'un think tank indépendant.",
        tags: ["Architecture de l'information", 'UX/UI', 'Collaboration externe'],
        url: 'https://ia.polemia.com/',
        animationKey: 'search',
      },
      {
        number: '02',
        title: 'Plateforme link-in-bio éthique et souveraine (en cours)',
        client: 'Projet professionnel',
        year: '2026',
        description:
          "Conception d'un SaaS link-in-bio pensé comme une alternative éthique aux solutions existantes. Travail sur l'ensemble de la chaîne UI et UX, compliance, enjeux de souveraineté, de durabilité et de protection des usages.",
        tags: ['Concept', "Architecture de l'information", 'UX/UI', 'Collaboration technique'],
        animationKey: 'linkInBio',
      },
      {
        number: '03',
        title: "Activité de webpublishing et d'illustration de presse assistée par IA",
        client: 'Ligne de Presse',
        year: '2024–2026',
        description:
          "Développement d'une activité éditoriale dédiée à la production et à la diffusion d'illustrations de presse assistées par IA. Travail mené sur la conception graphique, la cohérence stylistique, la chaîne de publication et l'adaptation des visuels aux contraintes éditoriales et journalistiques.",
        tags: ['Direction artistique', 'Webpublishing', 'Illustration IA'],
        url: 'https://lignedepresse.com/',
        animationKey: 'illustration',
      },
      {
        number: '04',
        title: 'Refonte du site officiel de la pianiste Katerina Verbovskaya',
        client: 'Katerina Verbovskaya',
        year: '2025–2026',
        description:
          "Conception et déploiement d'un site vitrine institutionnel, pensé comme un outil de représentation professionnelle. Travail mené sur l'architecture de l'information, la hiérarchie des contenus, l'identité visuelle, la performance technique et l'intégration d'un site statique optimisé pour la diffusion internationale.",
        tags: ["Architecture de l'information", 'UX/UI', 'Développement front-end'],
        url: 'https://katerinaverbovskaya.com/',
        animationKey: 'piano',
      },
    ],
  },
  gallery: {
    sectionTitle: 'Galerie',
    moreInfo: "Plus d'informations :",
    captions: {
      video:
        "Projet expérimental mêlant création visuelle, audio et montage, pensé pour des usages brand content / campagne sportswear. Usage d'IA spécialisées (Kive + Suno). Édition dans Adobe Première et Audition.",
      illu01:
        "Illustration éditoriale pour la presse en ligne. Usage d'IA spécialisée + Photoshop.",
      illu04:
        "Conception et direction artistique de l'habillage graphique d'un album de musique classique. CD pensé comme un objet d'art à part entière, s'éloignant des codes traditionnels du classique. Travail sur un design contemporain, un packaging soigné et cohérent. Approche transversale mêlant direction artistique, graphisme éditorial et sens de l'objet, avec une attention particulière portée à la matérialité, à la lisibilité et à la valeur esthétique du support physique. Photoshop + Illustrator.",
      illu02:
        "Illustration éditoriale pour la presse papier. Usage d'IA spécialisée + Photoshop.",
      foulards:
        "Conception de foulards en soie haut de gamme : transposition d'œuvres d'art en pièces textiles, développées en collaboration avec un artisan soyeux lyonnais d'excellence.",
    },
  },
  contact: {
    title: 'Travaillons ensemble',
    subtitle: 'Ouvert aux échanges professionnels et aux projets de long terme.',
    cta: 'Prendre contact',
  },
  stickyHeader: {
    darkLabel: 'Activer le mode clair',
    lightLabel: 'Activer le mode sombre',
  },
  nav: {
    intro: 'Intro',
    profile: 'Profil',
    experience: 'Expérience',
    education: 'Formation',
    skills: 'Compétences',
    projects: 'Projets',
    gallery: 'Galerie',
    contact: 'Contact',
  },
  customization: {
    typography: 'Typographie',
    color: 'Couleur',
    gradient: 'Gradient',
    density: 'Densité',
    fontSans: 'Sans',
    fontSerif: 'Serif',
    fontMono: 'Mono',
    colorSilver: 'Argent',
    colorOcean: 'Océan',
    colorForest: 'Forêt',
    colorGold: 'Or',
    gradientSubtle: 'Subtil',
    gradientSubtleDesc: 'Doux et discret',
    gradientVivid: 'Vif',
    gradientVividDesc: 'Plus prononcé',
    gradientMinimal: 'Minimal',
    gradientMinimalDesc: 'Presque invisible',
    densityCompact: 'Compact',
    densityNormal: 'Normal',
    densityAiry: 'Aéré',
  },
};

/* ------------------------------------------------------------------ */
/*  EN                                                                 */
/* ------------------------------------------------------------------ */

const en: Translations = {
  header: {
    subtitle: 'Senior Consultant',
    subtitleSuffix: 'Digital Solutions & UX',
    location: 'Valais, Switzerland',
    scrollLabel: 'Scroll down',
  },
  profile: {
    sectionTitle: 'Profile',
    intro:
      'Senior consultant with a strong artistic background, specialising in digital technologies. Over fifteen years of experience supporting SMEs, foundations, cultural organisations and European institutions in the design, development and long-term maintenance of web platforms.',
    details:
      'Broad skill set covering advanced WordPress, UX/UI, conversion funnels, technical optimisation, and graphic & video production, delivering comprehensive solutions across the full project lifecycle.',
  },
  experience: {
    sectionTitle: 'Professional Experience',
    items: [
      {
        period: '2010 — Present',
        role: 'Senior Digital Solutions & UX Consultant',
        company: 'Freelance',
        location: 'France, Switzerland, Belgium, Central Europe',
        mission: 'Digital support for foundations, artists and organisations across Europe.',
        activities: [
          'Design, development and maintenance of complex WordPress platforms',
          'UX/UI redesign, mobile optimisation and technical improvements',
          'Form and payment funnel creation (one-off and recurring)',
          'Graphic and digital content production (illustration, video)',
          'Technical SEO, performance optimisation and security',
          'Internal tooling: extranets, dashboards, automations',
          'Long-term technical support and advisory for management teams',
        ],
        valueAdded:
          'Delivering complete, stable and sustainable solutions to organisations without an in-house digital team, or seeking to strengthen their technical capabilities.',
      },
      {
        period: '2014 — 2015',
        role: 'Accredited Parliamentary Assistant — Digital Communications',
        company: 'European Parliament',
        location: 'Brussels',
        mission: 'Day-to-day management of digital communications.',
        activities: [
          'Content production (web, newsletters, social media)',
          'Video and motion design',
          'Editorial coordination',
        ],
      },
      {
        period: '2005 — 2010',
        role: 'Executive & Communications Assistant',
        company: 'Restaurant / Gallery 1728',
        location: 'Paris',
        activities: [
          'Administrative support',
          'Online and offline communications',
          'Event planning',
          'Visual content management',
          'Wine cellar management',
        ],
      },
    ],
  },
  education: {
    sectionTitle: 'Education',
    items: [
      { year: '2003 — 2005', degree: 'Multimedia Designer-Developer', institution: 'IESA Multimedia, Paris' },
      { year: '1999 — 2001', degree: 'Law', institution: 'Université Panthéon-Assas, Paris', note: 'Incomplete degree' },
      { year: '2001 — 2002', degree: 'First Certificate', institution: 'British Council, Paris' },
      { year: '1999', degree: 'French Baccalaureate (Literature stream)', institution: 'École Massillon, Paris' },
    ],
  },
  skills: {
    sectionTitle: 'Skills',
    categories: [
      {
        title: 'Project Management',
        skills: [
          'Digital project management (needs analysis, planning, reporting)',
          'Iterative approach: MVP, short sprints, continuous improvement',
          'Coordination with management, business teams and contractors',
          'Documentation, functional specifications, quality assurance',
        ],
      },
      {
        title: 'UX / UI & Design',
        skills: [
          'UX audit, user-journey redesign, conversion optimisation',
          'Graphic design (Illustrator, Photoshop, InDesign)',
          'Editorial illustration (AI + retouching)',
          'Video editing (Premiere Pro)',
        ],
      },
      {
        title: 'Development & Integration',
        skills: [
          'Advanced WordPress: custom modules, optimisation, security',
          'Front-end development (HTML / CSS / JS)',
          'Payment system integration (Stripe, online & recurring payments)',
          'Technical optimisation: performance, SEO, security, email deliverability',
        ],
      },
      {
        title: 'Digital Marketing',
        skills: [
          'Technical and editorial SEO',
          'Email marketing, newsletters, segmentation',
          'Multilingual content management',
        ],
      },
    ],
    languagesTitle: 'Languages',
    languages: [
      { name: 'French', level: 'Native' },
      { name: 'English', level: 'Advanced' },
      { name: 'Russian', level: 'Intermediate' },
    ],
  },
  projects: {
    sectionTitle: 'Projects',
    viewProject: 'View project →',
    linkSoon: 'Link coming soon',
    items: [
      {
        number: '01',
        title: 'AI-powered Political & Metapolitical Analysis Platform (V2.5)',
        client: 'Polémia',
        year: '2026',
        description:
          'Original concept. Long-running collaboration on content architecture, user journeys and the consultation experience for complex editorial corpora (50,000 sources). Built for an independent think tank.',
        tags: ['Information Architecture', 'UX/UI', 'External Collaboration'],
        url: 'https://ia.polemia.com/',
        animationKey: 'search',
      },
      {
        number: '02',
        title: 'Ethical & Sovereign Link-in-Bio Platform (in progress)',
        client: 'Professional project',
        year: '2026',
        description:
          'Designing a link-in-bio SaaS conceived as an ethical alternative to existing solutions. End-to-end UI/UX work, compliance, sovereignty, sustainability and user-protection considerations.',
        tags: ['Concept', 'Information Architecture', 'UX/UI', 'Technical Collaboration'],
        animationKey: 'linkInBio',
      },
      {
        number: '03',
        title: 'AI-Assisted Press Illustration & Web Publishing',
        client: 'Ligne de Presse',
        year: '2024–2026',
        description:
          'Building an editorial activity dedicated to producing and distributing AI-assisted press illustrations. Work spanning graphic design, stylistic consistency, the publishing pipeline and adaptation to editorial and journalistic requirements.',
        tags: ['Art Direction', 'Web Publishing', 'AI Illustration'],
        url: 'https://lignedepresse.com/',
        animationKey: 'illustration',
      },
      {
        number: '04',
        title: "Redesign of Pianist Katerina Verbovskaya's Official Website",
        client: 'Katerina Verbovskaya',
        year: '2025–2026',
        description:
          'Design and deployment of an institutional showcase website conceived as a professional representation tool. Work on information architecture, content hierarchy, visual identity, technical performance and integration of a static site optimised for international reach.',
        tags: ['Information Architecture', 'UX/UI', 'Front-end Development'],
        url: 'https://katerinaverbovskaya.com/',
        animationKey: 'piano',
      },
    ],
  },
  gallery: {
    sectionTitle: 'Gallery',
    moreInfo: 'More information:',
    captions: {
      video:
        'Experimental project blending visual creation, audio and editing, designed for brand content / sportswear campaign use. Specialised AI tools (Kive + Suno). Edited in Adobe Premiere and Audition.',
      illu01:
        'Editorial illustration for online media. Specialised AI + Photoshop.',
      illu04:
        "Art direction and graphic design for a classical music album. The CD was conceived as a standalone art object, breaking away from traditional classical music codes. Work on contemporary design, refined and coherent packaging. A cross-disciplinary approach combining art direction, editorial graphics and a feel for the physical object, with special attention to materiality, readability and the aesthetic value of the physical format. Photoshop + Illustrator.",
      illu02:
        'Editorial illustration for print media. Specialised AI + Photoshop.',
      foulards:
        "Design of luxury silk scarves: transposing artworks into textile pieces, developed in collaboration with a master silk artisan from Lyon.",
    },
  },
  contact: {
    title: "Let's work together",
    subtitle: 'Open to professional discussions and long-term projects.',
    cta: 'Get in touch',
  },
  stickyHeader: {
    darkLabel: 'Switch to light mode',
    lightLabel: 'Switch to dark mode',
  },
  nav: {
    intro: 'Intro',
    profile: 'Profile',
    experience: 'Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    gallery: 'Gallery',
    contact: 'Contact',
  },
  customization: {
    typography: 'Typography',
    color: 'Colour',
    gradient: 'Gradient',
    density: 'Density',
    fontSans: 'Sans',
    fontSerif: 'Serif',
    fontMono: 'Mono',
    colorSilver: 'Silver',
    colorOcean: 'Ocean',
    colorForest: 'Forest',
    colorGold: 'Gold',
    gradientSubtle: 'Subtle',
    gradientSubtleDesc: 'Soft and discreet',
    gradientVivid: 'Vivid',
    gradientVividDesc: 'More pronounced',
    gradientMinimal: 'Minimal',
    gradientMinimalDesc: 'Almost invisible',
    densityCompact: 'Compact',
    densityNormal: 'Normal',
    densityAiry: 'Airy',
  },
};

/* ------------------------------------------------------------------ */
/*  Helper                                                             */
/* ------------------------------------------------------------------ */

const translations: Record<Lang, Translations> = { fr, en };

export const t = (lang: Lang) => translations[lang];
export default translations;
